import { useState } from 'react';
import { useForm, useStore } from '@tanstack/react-form';
import {
  Accordion,
  AccordionItem,
  Button,
  Checkbox,
  IconButton,
  Select,
  SelectItem,
  TextInput,
} from '@carbon/react';
import {
  Copy,
  Save,
  SidePanelClose,
  SidePanelOpen,
  TrashCan,
} from '@carbon/react/icons';
import { PageHeader } from 'components/page-header';
import {
  ActivityFeedPanel,
  ActivityFeedPanelOpenButton,
} from 'components/activity-feed-panel';
import { usePricingEnums } from 'pages/pricing/shared/use-pricing-enums';
import {
  LegCompositionEditor,
  combinedFormulaText,
} from 'pages/pricing/shared/leg-composition-editor';
import type { Contract, CurveOption, Leg } from 'pages/pricing/shared/types';
import { parseUTCdateTime } from 'utils/dates';
import styles from './contract-editor.module.scss';

interface ContractFormData {
  name: string;
  commodity: string;
  legs: Leg[];
}

// Backend timestamps are UTC; format them in UTC so the date can't drift a day near midnight.
const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  return parseUTCdateTime(iso);
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.auditField}>
      <span className={styles.auditLabel}>{label}</span>
      <span className={styles.auditValue}>{value}</span>
    </div>
  );
}

interface Props {
  contract: Contract;
  isNew?: boolean; // an unsaved draft (the `/new` route) — create on Save, no Duplicate/Delete/Comments
  curveOptions: CurveOption[];
  onSave: (payload: Partial<Contract>) => Promise<Contract>;
  onDuplicate: () => void;
  onRequestDelete: () => void;
}

// The default name the list "Add" seeds a draft with — Save stays disabled until it's changed.
const isPlaceholderName = (name: string) =>
  /^New contract(\s+\d+)?$/i.test(name.trim());

type SectionKey = 'details' | 'legs' | 'formula';
const SECTIONS: { key: SectionKey; label: string }[] = [
  { key: 'details', label: 'Details' },
  { key: 'legs', label: 'Pricing legs' },
  { key: 'formula', label: 'Combined formula' },
];

export function ContractEditor({
  contract,
  isNew = false,
  curveOptions,
  onSave,
  onDuplicate,
  onRequestDelete,
}: Props) {
  const { enums } = usePricingEnums();
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [visible, setVisible] = useState<Record<SectionKey, boolean>>({
    details: true,
    legs: true,
    formula: true,
  });

  const form = useForm({
    defaultValues: {
      name: contract.name,
      commodity: contract.commodity ?? '',
      legs: contract.legs,
    } as ContractFormData,
    onSubmit: async ({ value }) => {
      const saved = await onSave({
        name: value.name.trim() || 'Untitled contract',
        status: 'published',
        commodity: value.commodity,
        legs: value.legs,
      });
      form.reset({
        name: saved.name,
        commodity: saved.commodity ?? '',
        legs: saved.legs,
      });
    },
  });

  const values = useStore(form.store, (s) => s.values);
  const { isDirty, isSubmitting } = useStore(form.store, (s) => ({
    isDirty: s.isDirty,
    isSubmitting: s.isSubmitting,
  }));

  // Save is gated on a real, non-placeholder name (the user must rename a fresh draft away from
  // "New contract N"), plus an actual change (a new draft is savable once renamed). The nameError
  // string both blocks Save and drives the field's red-border/invalidText (mirroring the Market
  // Data view name field).
  const trimmedName = values.name.trim();
  const nameError =
    trimmedName === ''
      ? 'Enter a name to save.'
      : isPlaceholderName(trimmedName)
        ? 'Rename this contract before saving.'
        : '';
  const canSave = nameError === '' && (isNew || isDirty) && !isSubmitting;

  return (
    <div className={styles.root}>
      <PageHeader
        breadcrumbs={[
          ['/pricing/contract-builder', 'Pricing Contracts'],
          [
            isNew
              ? '/pricing/contract-builder/new'
              : `/pricing/contract-builder/${contract.id}`,
            values.name || (isNew ? 'New contract' : 'Untitled contract'),
          ],
        ]}
        title={values.name || (isNew ? 'New contract' : 'Untitled contract')}
        actions={
          <div className={styles.headerActions}>
            <Button
              kind="primary"
              size="md"
              renderIcon={Save}
              disabled={!canSave}
              onClick={() => form.handleSubmit()}
            >
              {isSubmitting ? 'Saving…' : 'Save'}
            </Button>
            {!isNew && (
              <>
                <Button
                  kind="tertiary"
                  size="md"
                  renderIcon={Copy}
                  onClick={onDuplicate}
                >
                  Duplicate
                </Button>
                <Button
                  kind="danger--tertiary"
                  size="md"
                  renderIcon={TrashCan}
                  onClick={onRequestDelete}
                >
                  Delete
                </Button>
              </>
            )}
            <span className={styles.divider} aria-hidden />
            <IconButton
              kind="tertiary"
              size="md"
              label={
                panelCollapsed ? 'Show control panel' : 'Hide control panel'
              }
              align="bottom-right"
              onClick={() => setPanelCollapsed((v) => !v)}
            >
              {panelCollapsed ? <SidePanelOpen /> : <SidePanelClose />}
            </IconButton>
            {!isNew && <ActivityFeedPanelOpenButton />}
          </div>
        }
      />

      <div className={styles.body}>
        {!panelCollapsed && (
          <aside className={styles.panel}>
            <Accordion>
              <AccordionItem title="Layout" open>
                <p className={styles.sectionsLabel}>Sections</p>
                {SECTIONS.map((s) => (
                  <Checkbox
                    key={s.key}
                    id={`section-${s.key}`}
                    labelText={s.label}
                    checked={visible[s.key]}
                    onChange={() =>
                      setVisible((p) => ({ ...p, [s.key]: !p[s.key] }))
                    }
                  />
                ))}
              </AccordionItem>
            </Accordion>
          </aside>
        )}

        <div className={styles.content}>
          {visible.details && (
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Details</h2>
              <div className={styles.detailsForm}>
                <TextInput
                  id="ct-name"
                  labelText="Contract name"
                  value={values.name}
                  onChange={(e) => form.setFieldValue('name', e.target.value)}
                  invalid={nameError !== ''}
                  invalidText={nameError}
                />
                <Select
                  id="ct-commodity"
                  labelText="Commodity"
                  value={values.commodity}
                  onChange={(e) =>
                    form.setFieldValue('commodity', e.target.value)
                  }
                >
                  <SelectItem value="" text="—" />
                  {(enums?.commodities ?? []).map((o) => (
                    <SelectItem key={o.value} value={o.value} text={o.label} />
                  ))}
                </Select>
              </div>
              <div className={styles.auditGrid}>
                <Field label="Legs" value={String(values.legs.length)} />
                <Field label="Created by" value={contract.createdBy ?? '—'} />
                <Field label="Created" value={fmtDate(contract.createdAt)} />
                <Field
                  label="Last updated"
                  value={fmtDate(contract.updatedAt)}
                />
              </div>
            </section>
          )}

          {visible.legs && (
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Pricing legs</h2>
              <LegCompositionEditor
                legs={values.legs}
                curveOptions={curveOptions}
                onChange={(legs) => form.setFieldValue('legs', legs)}
                formulaName={values.name || 'Contract'}
                hideFormula
              />
            </section>
          )}

          {visible.formula && (
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Combined formula</h2>
              <p className={styles.formulaSub}>Legs summed in order.</p>
              <code className={styles.formulaCode}>
                {combinedFormulaText(values.legs, curveOptions, values.name)}
              </code>
            </section>
          )}

          {!visible.details && !visible.legs && !visible.formula && (
            <div className={styles.allHidden}>
              <p className={styles.emptyTitle}>All sections hidden</p>
              <p className={styles.emptyHint}>
                Turn a section back on from Layout in the panel.
              </p>
            </div>
          )}
        </div>

        {!isNew && (
          <ActivityFeedPanel
            modelType="Pricing::Contract"
            modelId={contract.id}
            title="Activity"
            subtitle={values.name || 'Untitled contract'}
            commentsHref={`/pricing/contracts/${contract.id}/comments`}
          />
        )}
      </div>
    </div>
  );
}
